import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import useUserStore from "../../../../../store/user";
import type { Profile } from "../types";
import { minisiteApi, capaApi, type MinisiteConfig, type CapaSugerida }
  from "../../../services/minisite";

// Módulos portados do georanking-minisite. São a FONTE ÚNICA dos modelos: se a
// aba tivesse a própria lista, o preview mentiria em relação à página pública.
// @ts-expect-error — JS puro sem tipos, portado do repo do mini-site
import MiniSite from "../minisite/MiniSite.jsx";
// @ts-expect-error — JS puro sem tipos, portado do repo do mini-site
import { TEMPLATES, getTemplate, sugerirTemplate } from "../minisite/templates";
// @ts-expect-error — JS puro sem tipos, portado do repo do mini-site
import { gerarVariacoes, totalVariacoes, NICHOS } from "../minisite/variacoes";
// @ts-expect-error — JS puro sem tipos, portado do repo do mini-site
import { extrairPaleta, paletaParaCores, mesclarTema, CORES_RAPIDAS, tintaSobre } from "../minisite/tema";
// @ts-expect-error — JS puro sem tipos, portado do repo do mini-site
import { FONTES, GOOGLE_FONTS_URL } from "../minisite/fontes";
// @ts-expect-error — JS puro sem tipos, portado do repo do mini-site
import { revisarTema } from "../minisite/revisar";

type Props = { profile: Profile; onVoltar: () => void };

type Tpl = { id: string; nome: string; nicho: string; t: Record<string, string> };
type Variacao = { id: string; nome?: string; modo?: string; t?: Record<string, string> };

/**
 * Carrega o que o MiniSite precisa para renderizar igual à página pública:
 * as 28 famílias de texto e o Material Symbols Rounded dos ícones.
 *
 * O app do GeoRanking só carrega "Material Icons", que é outra fonte e não
 * tem a classe .ms-ico — sem isto o preview mostrava o NOME do ícone como
 * texto ("chat Falar no WhatsApp", "near_me Como chegar").
 */
function useFontesDoMinisite() {
  useEffect(() => {
    const alvos: Array<[string, string]> = [
      ["gr-minisite-fonts", GOOGLE_FONTS_URL],
      ["gr-minisite-symbols",
       "https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@24,400,1,0&display=block"],
    ];
    for (const [id, href] of alvos) {
      if (document.getElementById(id)) continue;
      const l = document.createElement("link");
      l.id = id; l.rel = "stylesheet"; l.href = href;
      document.head.appendChild(l);
    }
    const ID_CSS = "gr-minisite-ms-ico";
    if (!document.getElementById(ID_CSS)) {
      const st = document.createElement("style");
      st.id = ID_CSS;
      st.textContent = ".ms-ico{font-family:'Material Symbols Rounded';font-weight:400;"
        + "font-style:normal;line-height:1;display:inline-block;flex:none;"
        + "font-variation-settings:'FILL' 1}";
      document.head.appendChild(st);
    }
  }, []);
}

/** Dados do cliente no formato que o MiniSite espera (o `d`). */
/** Ícones que o mini-site sabe desenhar (Material Symbols). */
const ICONES_LINK = [
  { id: "link", nome: "Automático" },
  { id: "chat", nome: "WhatsApp" },
  { id: "call", nome: "Telefone" },
  { id: "near_me", nome: "Como chegar" },
  { id: "star", nome: "Avaliar" },
  { id: "photo_camera", nome: "Instagram" },
  { id: "thumb_up", nome: "Facebook" },
  { id: "shopping_bag", nome: "Catálogo" },
  { id: "restaurant_menu", nome: "Menu" },
  { id: "event", nome: "Agendar" },
  { id: "language", nome: "Site" },
  { id: "play_circle", nome: "Vídeo" },
  { id: "location_on", nome: "Endereço" },
  { id: "description", nome: "Orçamento" },
] as const;

const FORMAS_LOGO = [
  { id: "circulo", nome: "Redonda" },
  { id: "arredondado", nome: "Cantos suaves" },
  { id: "quadrado", nome: "Quadrada" },
  { id: "livre", nome: "Sem moldura" },
] as const;

function montarDados(profile: Profile, cfg: MinisiteConfig) {
  const iniciais = (profile.name || "")
    .split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  const rev = (profile.reviews || []) as Array<{ author?: string; rating?: number; text?: string }>;
  return {
    nome: profile.name || "",
    iniciais,
    categoria: String(cfg.categoriaExibida || cfg.categoria || profile.category || ""),
    sobre: String(cfg.sobre || profile.description || ""),
    areaTexto: String(cfg.areaTexto || ""),
    telefone: profile.phone || "",
    whatsapp: profile.whatsapp || profile.phone || "",
    endereco: profile.address || "",
    cidade: profile.city || "",
    logoUrl: String(cfg.logoUrl || profile.logoUrl || ""),
    logoForma: String(cfg.logoForma || "circulo"),
    logoAjuste: String(cfg.logoAjuste || "contain"),
    logoTamanho: Number(cfg.logoTamanho) > 0 ? Number(cfg.logoTamanho) : 88,
    capa: String(cfg.capa || ""),
    fotos: (cfg.galeria as string[] | undefined) || profile.photos || [],
    // MiniSite le r.rating / r.text / r.author — mandar autor/nota/texto
    // deixava os depoimentos em branco com nota 5 fixa.
    reviews: rev,
    nota: profile.rating,
    totalAvaliacoes: profile.reviewCount,
    reviewUrl: profile.reviewUrl || "",
    horarioTexto: "",
    servicos: [],
    links: (cfg.links as Array<{ texto: string; url: string; icone?: string }>) || [],
    ctaWhatsapp: "Falar no WhatsApp",
    tituloGaleria: "Fotos",
  };
}

export function EditorMinisite({ profile, onVoltar }: Props) {
  useFontesDoMinisite();

  const [cfg, setCfg] = useState<MinisiteConfig>({ publicado: false, indexar: false });
  const [slug, setSlug] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ erro: boolean; texto: string } | null>(null);
  // endereço publicado que o SERVIDOR já conhece — não o que está na tela
  const [publicadoEm, setPublicadoEm] = useState<string | null>(null);
  const [dispositivo, setDispositivo] = useState<"celular" | "desktop">("celular");
  const [revisao, setRevisao] = useState<
    { ok: boolean; itens: Array<{ rotulo: string; detalhe: string }> } | null>(null);
  const [nicho, setNicho] = useState("universal");
  const [capas, setCapas] = useState<CapaSugerida[] | null>(null);
  const [buscandoCapas, setBuscandoCapas] = useState(false);
  const [termoCapa, setTermoCapa] = useState("");

  type LinkBotao = { texto: string; url: string; icone?: string };
  const links = ((cfg.links as LinkBotao[] | undefined) || []);
  const escreverLinks = useCallback((novos: LinkBotao[]) => {
    setCfg((c) => ({ ...c, links: novos }));
  }, []);
  const adicionarLink = useCallback(() => {
    escreverLinks([...((cfg.links as LinkBotao[] | undefined) || []),
      { texto: "", url: "", icone: "link" }]);
  }, [cfg.links, escreverLinks]);
  const mudarLink = useCallback((i: number, campo: Partial<LinkBotao>) => {
    const atuais = [...((cfg.links as LinkBotao[] | undefined) || [])];
    atuais[i] = { ...atuais[i], ...campo };
    escreverLinks(atuais);
  }, [cfg.links, escreverLinks]);
  const removerLink = useCallback((i: number) => {
    escreverLinks(((cfg.links as LinkBotao[] | undefined) || []).filter((_, k) => k !== i));
  }, [cfg.links, escreverLinks]);
  const moverLink = useCallback((i: number, dir: number) => {
    const atuais = [...((cfg.links as LinkBotao[] | undefined) || [])];
    const j = i + dir;
    if (j < 0 || j >= atuais.length) return;
    [atuais[i], atuais[j]] = [atuais[j], atuais[i]];
    escreverLinks(atuais);
  }, [cfg.links, escreverLinks]);
  const inputLogo = useRef<HTMLInputElement>(null);
  const inputCapa = useRef<HTMLInputElement>(null);

  const recomendado: string = useMemo(
    () => sugerirTemplate(profile.category || ""),
    [profile.category],
  );

  useEffect(() => {
    let morto = false;
    minisiteApi.get(profile.id)
      .then((r) => {
        if (morto) return;
        if (r?.config) {
          setCfg({ publicado: false, indexar: false, ...r.config });
          setSlug(String(r.config.slug || ""));
          setPublicadoEm(r.config.publicado && r.config.slug ? String(r.config.slug) : null);
        } else {
          // começa no modelo recomendado para a categoria, não em branco
          setCfg((c) => ({ ...c, template: recomendado }));
        }
      })
      .catch(() => { if (!morto) setAviso({ erro: true, texto: "Não foi possível carregar." }); })
      .finally(() => { if (!morto) setCarregando(false); });
    return () => { morto = true; };
  }, [profile.id, recomendado]);

  const templateAtual: Tpl = getTemplate(cfg.template || recomendado);
  // Só os OVERRIDES ficam salvos; os tokens são remontados a cada render, para a
  // página herdar melhorias futuras do template em vez de congelar na versão salva.
  const tema = useMemo(
    () => mesclarTema(templateAtual.t, { ...(cfg.cores || {}), ...(cfg.fontes ? {
      font: cfg.fontes.texto, headFont: cfg.fontes.titulo,
    } : {}) }),
    [templateAtual, cfg.cores, cfg.fontes],
  );
  const dados = useMemo(() => montarDados(profile, cfg), [profile, cfg]);
  const variacoes: Variacao[] = useMemo(() => gerarVariacoes(nicho, 36), [nicho]);

  const aoEscolherLogo = useCallback((arq?: File) => {
    if (!arq) return;
    const fr = new FileReader();
    fr.onload = () => {
      const dataUrl = String(fr.result);
      setCfg((c) => ({ ...c, logoUrl: dataUrl }));
      // paleta da logo -> cores da marca. É o que separa "template" de "site do
      // cliente". extrairPaleta é PROMISE (não callback): com callback a paleta
      // era ignorada em silêncio e a logo não mudava cor nenhuma.
      Promise.resolve(extrairPaleta(dataUrl))
        .then((paleta: string[]) => {
          if (!paleta?.length) {
            setAviso({ erro: true, texto: "Não consegui ler as cores dessa imagem." });
            return;
          }
          const cores = paletaParaCores(paleta, templateAtual.t) as Record<string, string>;
          setCfg((c) => ({ ...c, cores: { ...(c.cores || {}), ...cores } }));
          setAviso({ erro: false, texto: "Cores aplicadas a partir da logo." });
        })
        .catch(() => setAviso({ erro: true, texto: "Não consegui ler as cores dessa imagem." }));
    };
    fr.readAsDataURL(arq);
  }, [templateAtual]);

  /**
   * Termo de busca a partir do ramo do negócio. Palavras de pessoa jurídica
   * ("empresa", "comércio", "ltda") sequestram a busca: com elas o acervo
   * devolve prédio de escritório; sem elas devolve o serviço em si.
   */
  const termoSugerido = useMemo(() => {
    const RUIDO = new Set([
      "empresa", "empresas", "companhia", "cia", "ltda", "me", "eireli",
      "comercio", "comércio", "loja", "lojas", "servico", "serviço",
      "servicos", "serviços", "de", "da", "do", "das", "dos", "e", "em",
    ]);
    const bruto = String(cfg.categoriaExibida || cfg.categoria || profile.category || "");
    const limpo = bruto
      .replace(/\s*[·|/]\s*/g, " ")
      .split(/\s+/)
      .filter((p) => p && !RUIDO.has(p.toLowerCase()))
      .join(" ")
      .trim();
    return limpo || bruto.trim() || profile.name || "";
  }, [cfg.categoriaExibida, cfg.categoria, profile.category, profile.name]);

  const buscarCapas = useCallback(async (termoBruto: string) => {
    const termo = termoBruto.trim();
    if (!termo) return;
    setBuscandoCapas(true);
    setAviso(null);
    try {
      const achadas = await capaApi.buscar(termo, 12);
      if (!achadas.length) {
        setAviso({ erro: true, texto: `Nenhuma foto para "${termo}". Tente outra palavra.` });
      }
      setCapas(achadas);
    } catch {
      setAviso({ erro: true, texto: "O acervo de fotos não respondeu. Tente de novo." });
    } finally {
      setBuscandoCapas(false);
    }
  }, []);

  const sugerirCapas = useCallback(() => {
    // DESLIGADO DE PROPOSITO (10/08/2026).
    // Mostrar as fotos do perfil aqui exige que o backend aceite o host
    // lh3.googleusercontent.com no download (PR #20 do georanking-api). Esse
    // deploy esta bloqueado: a main do backend esta 16 classes atras da
    // producao. Com o codigo ligado, o botao MOSTRARIA as fotos e o clique
    // falharia com "URL de imagem nao permitida" — pior que o estado atual,
    // em que o usuario simplesmente nao ve foto nenhuma aqui.
    // Religar assim que o backend com a lista de hosts estiver no ar.
    const t = termoCapa.trim() || termoSugerido;
    setTermoCapa(t);
    void buscarCapas(t);
  }, [termoCapa, termoSugerido, buscarCapas]);

  const usarCapaSugerida = useCallback(async (c: CapaSugerida) => {
    setBuscandoCapas(true);
    try {
      const dataUrl = await capaApi.paraDataUrl(c.fullUrl);
      if (!dataUrl) throw new Error("sem data url");
      setCfg((x) => ({ ...x, capa: dataUrl }));
      setCapas(null);
      setAviso({ erro: false, texto: `Capa aplicada (foto de ${c.photographer}).` });
    } catch {
      setAviso({ erro: true, texto: "Não consegui baixar essa foto. Escolha outra." });
    } finally {
      setBuscandoCapas(false);
    }
  }, []);

  const aoEscolherCapa = useCallback((arq?: File) => {
    if (!arq) return;
    const fr = new FileReader();
    fr.onload = () => setCfg((c) => ({ ...c, capa: String(fr.result) }));
    fr.readAsDataURL(arq);
  }, []);

  const revisar = useCallback(() => {
    // revisarTema devolve { problemas, correcoes, ok } — `correcoes` já é o mapa
    // de overrides pronto para o config. Não existe `t` no retorno.
    const { problemas, correcoes, ok } = revisarTema(tema) as {
      problemas: Array<{ rotulo: string; contraste?: number | null; minimo?: number | null }>;
      correcoes: Record<string, string>;
      ok: boolean;
    };
    if (ok) {
      setRevisao({ ok: true, itens: [] });
      return;
    }
    setCfg((c) => ({ ...c, cores: { ...(c.cores || {}), ...correcoes } }));
    setRevisao({
      ok: false,
      itens: problemas.map((p) => ({
        rotulo: p.rotulo,
        detalhe: p.contraste && p.minimo
          ? `contraste ${p.contraste.toFixed(1)} — mínimo ${p.minimo}`
          : "ajustado para o padrão",
      })),
    });
  }, [tema]);

  // Cliente vê e monta o mini-site, mas o backend só deixa a equipe salvar (PUT /minisite sem CUSTOMER).
  const ehCliente = useUserStore((estado) => estado.user?.userType === "CUSTOMER");

  const salvar = useCallback(async () => {
    setSalvando(true);
    setAviso(null);
    try {
      const corpo: MinisiteConfig = { ...cfg, slug: slug.trim().toLowerCase() || undefined };
      const r = await minisiteApi.save(profile.id, corpo);
      if (r?.config) setCfg((c) => ({ ...c, ...r.config }));
      const salvo = (r?.config || corpo) as MinisiteConfig;
      setPublicadoEm(salvo.publicado && salvo.slug ? String(salvo.slug) : null);
      setAviso({
        erro: false,
        texto: salvo.publicado
          ? `Salvo e publicado em ${String(salvo.slug)}.georanking.site`
          : "Salvo. A página ainda não está publicada.",
      });
    } catch (e) {
      const m = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setAviso({ erro: true, texto: m || "Não foi possível salvar." });
    } finally { setSalvando(false); }
  }, [profile.id, cfg, slug]);

  const sugerirSlug = () => setSlug(
    (profile.name || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40),
  );

  const nSobre = String(cfg.sobre || "").trim().length;
  const nArea = String(cfg.areaTexto || "").trim().length;
  const slugAtual = slug.trim().toLowerCase();
  const noAr = publicadoEm === slugAtual && Boolean(slugAtual);
  /** O que falta, na ordem em que a tela pede. `opcional` não bloqueia nada. */
  const passos = [
    { n: 1, nome: "Modelo", ok: true },
    { n: 2, nome: "Marca", ok: Boolean(cfg.logoUrl || cfg.cores?.accent), opcional: true },
    { n: 3, nome: "Textos", ok: nSobre >= 120 && nArea >= 80 },
    { n: 4, nome: "Links", ok: links.length > 0, opcional: true },
    { n: 5, nome: "Fotos", ok: Boolean(cfg.capa), opcional: true },
    { n: 6, nome: "Estilo", ok: Boolean(cfg.variacao), opcional: true },
    { n: 7, nome: "Publicar", ok: noAr },
  ];
  const irPara = (n: number) => {
    const alvo = document.getElementById(`passo-${n}`);
    if (!alvo) return;
    // o modal rola num container próprio; scrollIntoView parava na 1ª borda
    let caixa: HTMLElement | null = alvo.parentElement;
    while (caixa) {
      const cs = getComputedStyle(caixa);
      if (/auto|scroll/.test(cs.overflowY) && caixa.scrollHeight > caixa.clientHeight) break;
      caixa = caixa.parentElement;
    }
    if (!caixa) {
      alvo.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const destino = alvo.getBoundingClientRect().top
      - caixa.getBoundingClientRect().top + caixa.scrollTop;
    caixa.scrollTo({ top: Math.max(0, destino - 56), behavior: "smooth" });
  };
  const url = slugAtual ? `https://${slugAtual}.georanking.site` : null;

  return (
    <Shell>
      <Barra>
        <BotaoVoltar type="button" onClick={onVoltar}>
          <span className="material-icons" style={{ fontSize: 18 }}>arrow_back</span>Voltar
        </BotaoVoltar>
        <Titulo>Criador de mini-site</Titulo>
        <BotaoGhost type="button" onClick={revisar}>
          <span className="material-icons" style={{ fontSize: 17 }}>verified</span>Revisar design
        </BotaoGhost>
        {ehCliente ? (
          <span style={{ fontSize: 13, color: "#5F6368" }}>A equipe GeoRanking publica as mudanças</span>
        ) : (
          <BotaoPrimario type="button" onClick={salvar} disabled={salvando}>
            {salvando ? "Salvando…" : "Salvar"}
          </BotaoPrimario>
        )}
      </Barra>

      <Trilha>
        {passos.map((p) => (
          <Passo key={p.n} type="button" onClick={() => irPara(p.n)}
            $estado={p.ok ? "ok" : p.opcional ? "opcional" : "falta"}>
            <PassoBola $estado={p.ok ? "ok" : p.opcional ? "opcional" : "falta"}>
              {p.ok
                ? <span className="material-icons" style={{ fontSize: 14 }}>check</span>
                : p.n}
            </PassoBola>
            {p.nome}
          </Passo>
        ))}
      </Trilha>

      {aviso && (
        <PainelRevisao $ok={!aviso.erro}>
          <PainelTopo>
            <span className="material-icons" style={{ fontSize: 19 }}>
              {aviso.erro ? "error_outline" : "check_circle"}
            </span>
            <strong>{aviso.texto}</strong>
            <FecharPainel type="button" onClick={() => setAviso(null)} aria-label="Fechar">
              <span className="material-icons" style={{ fontSize: 17 }}>close</span>
            </FecharPainel>
          </PainelTopo>
        </PainelRevisao>
      )}

      {revisao && (
        <PainelRevisao $ok={revisao.ok}>
          <PainelTopo>
            <span className="material-icons" style={{ fontSize: 19 }}>
              {revisao.ok ? "check_circle" : "auto_fix_high"}
            </span>
            <strong>
              {revisao.ok
                ? "Design aprovado — todos os textos estão legíveis sobre o fundo."
                : `${revisao.itens.length} ajuste${revisao.itens.length > 1 ? "s" : ""} de legibilidade aplicado${revisao.itens.length > 1 ? "s" : ""}`}
            </strong>
            <FecharPainel type="button" onClick={() => setRevisao(null)} aria-label="Fechar">
              <span className="material-icons" style={{ fontSize: 17 }}>close</span>
            </FecharPainel>
          </PainelTopo>
          {revisao.ok ? (
            <PainelNota>
              Verifiquei o contraste do título, dos textos de apoio e do botão principal
              contra o fundo do modelo, no padrão WCAG AA.
            </PainelNota>
          ) : (
            <PainelLista>
              {revisao.itens.map((i, k) => (
                <li key={k}><b>{i.rotulo}</b> — {i.detalhe}</li>
              ))}
            </PainelLista>
          )}
        </PainelRevisao>
      )}

      {carregando ? <Nota>Carregando…</Nota> : (
        <Colunas>
          {/* ----------------- CONTROLES ----------------- */}
          <Controles>
            <Secao id="passo-1">
              <SecaoTitulo>1 · Modelo</SecaoTitulo>
              <SecaoAjuda>O visual da página. Dá para trocar depois sem perder nada.</SecaoAjuda>
              <GaleriaModelos>
                {(TEMPLATES as Tpl[]).map((tpl) => (
                  <CardModelo
                    key={tpl.id}
                    type="button"
                    $sel={(cfg.template || recomendado) === tpl.id}
                    onClick={() => setCfg((c) => ({ ...c, template: tpl.id, variacao: undefined }))}
                  >
                    <MiniPreviewBox>
                      <MiniPreviewEscala>
                        <MiniSite t={mesclarTema(tpl.t, cfg.cores || {})} d={dados} preview />
                      </MiniPreviewEscala>
                    </MiniPreviewBox>
                    <CardModeloNome>
                      {tpl.nome}
                      {tpl.id === recomendado && <Recomendado>Recomendado</Recomendado>}
                    </CardModeloNome>
                    <CardModeloNicho>{tpl.nicho}</CardModeloNicho>
                  </CardModelo>
                ))}
              </GaleriaModelos>
            </Secao>

            <Secao id="passo-2">
              <SecaoTitulo>2 · Marca</SecaoTitulo>
              <SecaoAjuda>Logo e cor. A logo define as cores sozinha.</SecaoAjuda>
              <LinhaBotoes>
                <BotaoGhost type="button" onClick={() => inputLogo.current?.click()}>
                  <span className="material-icons" style={{ fontSize: 17 }}>upload</span>
                  {cfg.logoUrl ? "Trocar logo" : "Subir logo"}
                </BotaoGhost>
                <input ref={inputLogo} type="file" accept="image/*" hidden
                  onChange={(e) => aoEscolherLogo(e.target.files?.[0])} />
                {Boolean(cfg.logoUrl) && (
                  <BotaoGhost type="button" onClick={() => setCfg((c) => ({ ...c, logoUrl: undefined }))}>
                    Remover
                  </BotaoGhost>
                )}
              </LinhaBotoes>
              <Dica>A logo define as cores automaticamente. Ajuste depois se quiser.</Dica>

              {Boolean(cfg.logoUrl) && (
                <>
                  <Rotulo style={{ marginTop: 10 }}>Moldura</Rotulo>
                  <LinhaBotoes>
                    {FORMAS_LOGO.map((f) => (
                      <Pilula key={f.id} type="button"
                        $sel={(cfg.logoForma || "circulo") === f.id}
                        onClick={() => setCfg((c) => ({ ...c, logoForma: f.id }))}>
                        {f.nome}
                      </Pilula>
                    ))}
                  </LinhaBotoes>

                  <Rotulo style={{ marginTop: 10 }}>Enquadramento</Rotulo>
                  <LinhaBotoes>
                    <Pilula type="button" $sel={(cfg.logoAjuste || "contain") === "contain"}
                      onClick={() => setCfg((c) => ({ ...c, logoAjuste: "contain" }))}>
                      Logo inteira
                    </Pilula>
                    <Pilula type="button" $sel={cfg.logoAjuste === "cover"}
                      onClick={() => setCfg((c) => ({ ...c, logoAjuste: "cover" }))}>
                      Preencher (corta)
                    </Pilula>
                  </LinhaBotoes>
                  <Dica>
                    &quot;Logo inteira&quot; nunca corta — é o certo para logo horizontal ou com texto.
                  </Dica>

                  <Rotulo style={{ marginTop: 10 }}>
                    Tamanho — {Number(cfg.logoTamanho) > 0 ? Number(cfg.logoTamanho) : 88}px
                  </Rotulo>
                  <Deslizante type="range" min={56} max={160} step={4}
                    value={Number(cfg.logoTamanho) > 0 ? Number(cfg.logoTamanho) : 88}
                    onChange={(e) => setCfg((c) => ({ ...c, logoTamanho: Number(e.target.value) }))} />
                </>
              )}
              <Rotulo style={{ marginTop: 12 }}>Cor principal</Rotulo>
              <Swatches>
                {(CORES_RAPIDAS as string[]).map((cor) => (
                  <Swatch key={cor} type="button" $cor={cor}
                    $sel={(cfg.cores?.accent || "").toLowerCase() === cor.toLowerCase()}
                    title={cor}
                    onClick={() => setCfg((c) => ({
                      ...c, cores: { ...(c.cores || {}), accent: cor, onAccent: tintaSobre(cor) },
                    }))} />
                ))}
              </Swatches>
            </Secao>

            <Secao id="passo-3">
              <SecaoTitulo>3 · Textos</SecaoTitulo>
              <SecaoAjuda>O que o Google lê. Sem texto próprio a página não é indexada.</SecaoAjuda>
              <Campo>
                <Rotulo>
                  Sobre o negócio
                  <Contador $ok={nSobre >= 120}>{nSobre}/120 para liberar o Google</Contador>
                </Rotulo>
                <Area rows={4} maxLength={800} value={String(cfg.sobre || "")}
                  placeholder="O que o negócio faz, há quanto tempo, diferenciais…"
                  onChange={(e) => setCfg((c) => ({ ...c, sobre: e.target.value }))} />
              </Campo>
              <Campo>
                <Rotulo>
                  Onde atendemos
                  <Contador $ok={nArea >= 80}>{nArea}/80 para liberar o Google</Contador>
                </Rotulo>
                <Area rows={3} maxLength={500} value={String(cfg.areaTexto || "")}
                  placeholder="Bairros, cidades e região atendidos…"
                  onChange={(e) => setCfg((c) => ({ ...c, areaTexto: e.target.value }))} />
              </Campo>
              <Campo>
                <Rotulo>Categoria exibida</Rotulo>
                <Entrada maxLength={60} value={String(cfg.categoriaExibida || "")}
                  placeholder={profile.category || "Ex.: Doces artesanais"}
                  onChange={(e) => setCfg((c) => ({ ...c, categoriaExibida: e.target.value }))} />
              </Campo>
              <Campo>
                <Rotulo>Fonte</Rotulo>
                <Select value={String(cfg.fontes?.titulo || tema.headFont || "")}
                  onChange={(e) => setCfg((c) => ({
                    ...c, fontes: { titulo: e.target.value, texto: e.target.value },
                  }))}>
                  {(FONTES as Array<{ grupo: string; itens: Array<{ nome: string; css: string }> }>).map((g) => (
                    <optgroup key={g.grupo} label={g.grupo}>
                      {g.itens.map((f) => <option key={f.css} value={f.css}>{f.nome}</option>)}
                    </optgroup>
                  ))}
                </Select>
              </Campo>
            </Secao>

            <Secao id="passo-4">
              <SecaoTitulo>4 · Links</SecaoTitulo>
              <SecaoAjuda>
                Os botões da página. Cadastrando pelo menos um, a página mostra só
                os seus — WhatsApp, telefone, localização e avaliação entram como
                você colocar aqui. Sem nenhum, a página usa os do perfil sozinha.
              </SecaoAjuda>
              {(links.length === 0) && (
                <Dica>Nenhum link ainda. É opcional, mas é o que deixa a página com cara de link da bio.</Dica>
              )}
              {links.map((l, i) => (
                <LinhaLink key={i}>
                  <Select value={l.icone || "link"} style={{ maxWidth: 132 }}
                    onChange={(e) => mudarLink(i, { icone: e.target.value })}>
                    {ICONES_LINK.map((o) => (
                      <option key={o.id} value={o.id}>{o.nome}</option>
                    ))}
                  </Select>
                  <Entrada value={l.texto} placeholder="Texto do botão"
                    style={{ flex: "1 1 140px" }}
                    onChange={(e) => mudarLink(i, { texto: e.target.value })} />
                  <Entrada value={l.url} placeholder="https://…"
                    style={{ flex: "1 1 180px" }}
                    onChange={(e) => mudarLink(i, { url: e.target.value })} />
                  <BotaoIcone type="button" title="Subir" disabled={i === 0}
                    onClick={() => moverLink(i, -1)}>
                    <span className="material-icons" style={{ fontSize: 17 }}>arrow_upward</span>
                  </BotaoIcone>
                  <BotaoIcone type="button" title="Remover" onClick={() => removerLink(i)}>
                    <span className="material-icons" style={{ fontSize: 17 }}>delete_outline</span>
                  </BotaoIcone>
                </LinhaLink>
              ))}
              <LinhaBotoes>
                <BotaoGhost type="button" onClick={adicionarLink} disabled={links.length >= 8}>
                  <span className="material-icons" style={{ fontSize: 17 }}>add</span>
                  Adicionar link
                </BotaoGhost>
              </LinhaBotoes>
              {links.length >= 8 && <Dica>Oito botões é o limite — acima disso ninguém clica.</Dica>}
            </Secao>

            <Secao id="passo-5">
              <SecaoTitulo>5 · Fotos</SecaoTitulo>
              <SecaoAjuda>A capa é o que aparece quando alguém manda o link no WhatsApp.</SecaoAjuda>
              <LinhaBotoes>
                <BotaoGhost type="button" onClick={() => inputCapa.current?.click()}>
                  <span className="material-icons" style={{ fontSize: 17 }}>image</span>
                  {cfg.capa ? "Trocar capa" : "Subir capa"}
                </BotaoGhost>
                <input ref={inputCapa} type="file" accept="image/*" hidden
                  onChange={(e) => aoEscolherCapa(e.target.files?.[0])} />
                <BotaoGhost type="button" onClick={sugerirCapas} disabled={buscandoCapas}>
                  <span className="material-icons" style={{ fontSize: 17 }}>auto_awesome</span>
                  {buscandoCapas ? "Buscando…" : "Sugerir capa"}
                </BotaoGhost>
                {Boolean(cfg.capa) && (
                  <BotaoGhost type="button" onClick={() => setCfg((c) => ({ ...c, capa: undefined }))}>
                    Remover
                  </BotaoGhost>
                )}
              </LinhaBotoes>

              {Boolean(cfg.capa) && (
                <CapaAtual src={String(cfg.capa)} alt="Capa escolhida" />
              )}

              {capas !== null && (
                <>
                  <Rotulo style={{ marginTop: 8 }}>Buscar foto de</Rotulo>
                  <LinhaSlug>
                    <Entrada value={termoCapa} placeholder={termoSugerido}
                      onChange={(e) => setTermoCapa(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sugerirCapas(); } }} />
                    <BotaoGhost type="button" disabled={buscandoCapas}
                      onClick={() => void buscarCapas(termoCapa.trim() || termoSugerido)}>
                      Buscar
                    </BotaoGhost>
                  </LinhaSlug>
                  <Dica>
                    Escreva o que a foto deve mostrar (ex.: painel solar, oficina,
                    barbearia). Palavras como &quot;empresa&quot; trazem foto de escritório.
                  </Dica>
                </>
              )}

              {capas && capas.length > 0 && (
                <>
                  <Rotulo style={{ marginTop: 8 }}>Escolha uma capa</Rotulo>
                  <GradeCapas>
                    {capas.map((c) => (
                      <CapaOpcao key={c.id} type="button" title={c.alt || c.photographer}
                        disabled={buscandoCapas} onClick={() => usarCapaSugerida(c)}>
                        <img src={c.thumbUrl} alt={c.alt || ""} loading="lazy" />
                      </CapaOpcao>
                    ))}
                  </GradeCapas>
                  <Dica>
                    {capas.some((c) => c.id < 0)
                      ? "Fotos do próprio perfil no Google. Clique para aplicar — ou busque no acervo acima."
                      : "Fotos livres de direitos. Clique para aplicar."}
                  </Dica>
                </>
              )}
              <Dica>
                A capa é a imagem que aparece quando o link é compartilhado no WhatsApp.
                Sem ela, aparece só um quadradinho com a logo.
              </Dica>
              <Dica>Galeria: {(profile.photos || []).length} fotos vindas do perfil do Google.</Dica>
            </Secao>

            <Secao id="passo-6">
              <SecaoTitulo>6 · Estilo</SecaoTitulo>
              <SecaoAjuda>Combinações de cor, fonte e forma prontas. Clique para aplicar.</SecaoAjuda>
              <Select value={nicho} onChange={(e) => setNicho(e.target.value)} style={{ maxWidth: 280 }}>
                {(NICHOS as Array<{ id: string; nome: string }>).map((n) => (
                  <option key={n.id} value={n.id}>{n.nome}</option>
                ))}
              </Select>
              <Dica>
                {totalVariacoes(nicho)} combinações de cor, fonte e forma para este
                nicho — mostrando 36. Cada cartão é a combinação aplicada.
              </Dica>
              <Variacoes>
                {variacoes.map((v) => {
                  const vt = v.t || {};
                  return (
                    <CartaoVariacao key={v.id} type="button" title={v.nome || v.id}
                      $sel={cfg.variacao === v.id}
                      onClick={() => setCfg((c) => ({
                        ...c, variacao: v.id,
                        // a variação entrega `t` (tokens completos), não cores/fontes:
                        // guardamos como overrides do template escolhido
                        cores: { ...(c.cores || {}), ...vt },
                      }))}
                    >
                      {/* amostra real da combinação: fundo, título na fonte do tema
                          e o botão de destaque — é o que o consultor precisa ver */}
                      <VarAmostra style={{ background: vt.bg || "#f1f3f4" }}>
                        <VarTitulo style={{ color: vt.ink || "#1F1F1F", fontFamily: vt.headFont || vt.font }}>
                          {profile.name?.slice(0, 14) || "Seu negócio"}
                        </VarTitulo>
                        <VarLinha style={{ background: vt.sub || "rgba(0,0,0,.25)" }} />
                        <VarBotao style={{ background: vt.accent || "#1264C7", color: vt.onAccent || "#fff" }}>
                          Falar agora
                        </VarBotao>
                      </VarAmostra>
                      <VarRodape>
                        <VarNome>{v.nome || v.id}</VarNome>
                        <VarModo $escuro={v.modo === "Escuro"}>{v.modo}</VarModo>
                      </VarRodape>
                    </CartaoVariacao>
                  );
                })}
              </Variacoes>
            </Secao>

            <Secao id="passo-7">
              <SecaoTitulo>7 · Publicar</SecaoTitulo>
              <SecaoAjuda>O endereço da página e o que ainda falta para ela ir ao ar.</SecaoAjuda>
              <Campo>
                <Rotulo>Endereço da página</Rotulo>
                <LinhaSlug>
                  <Entrada value={slug} placeholder="nome-do-cliente" maxLength={63}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} />
                  <Sufixo>.georanking.site</Sufixo>
                  <BotaoGhost type="button" onClick={sugerirSlug}>Sugerir</BotaoGhost>
                </LinhaSlug>
              </Campo>
              <Checklist>
                {[
                  { ok: Boolean(slugAtual), txt: "Endereço escolhido" },
                  { ok: nSobre >= 120, txt: `Texto sobre o negócio (${nSobre}/120)` },
                  { ok: nArea >= 80, txt: `Texto da área atendida (${nArea}/80)` },
                  { ok: Boolean(cfg.capa), txt: "Capa para o WhatsApp", opcional: true },
                  { ok: noAr, txt: "Publicado e salvo" },
                ].map((i, k) => (
                  <li key={k}>
                    <span className="material-icons"
                      style={{ fontSize: 16, color: i.ok ? "#1E8E3E" : i.opcional ? "#9aa0a6" : "#b06000" }}>
                      {i.ok ? "check_circle" : i.opcional ? "radio_button_unchecked" : "error_outline"}
                    </span>
                    <span style={{ color: i.ok ? "#1E8E3E" : "#2B2D31" }}>{i.txt}</span>
                    {i.opcional && !i.ok && <ItemOpcional>opcional</ItemOpcional>}
                  </li>
                ))}
              </Checklist>

              <Check>
                <input type="checkbox" checked={Boolean(cfg.publicado)}
                  onChange={(e) => setCfg((c) => ({ ...c, publicado: e.target.checked }))} />
                <span><strong>Publicar a página</strong>
                  <small>Sem isso o endereço responde como inexistente.</small></span>
              </Check>
              <Check>
                <input type="checkbox" checked={Boolean(cfg.indexar)} disabled={!cfg.publicado}
                  onChange={(e) => setCfg((c) => ({ ...c, indexar: e.target.checked }))} />
                <span><strong>Permitir que apareça no Google</strong>
                  <small>Precisa dos textos acima completos.</small></span>
              </Check>
              {publicadoEm === slugAtual && url ? (
                <BotaoLink href={url} target="_blank" rel="noopener noreferrer">
                  Abrir {url.replace("https://", "")}
                </BotaoLink>
              ) : Boolean(cfg.publicado) && slugAtual ? (
                <PendenteSalvar>
                  <span className="material-icons" style={{ fontSize: 17 }}>info</span>
                  {ehCliente
                    ? "Peça à equipe GeoRanking para publicar este endereço."
                    : publicadoEm
                      ? `No ar em ${publicadoEm}.georanking.site. Clique em Salvar para mudar o endereço para ${slugAtual}.`
                      : `Clique em Salvar para o endereço ${slugAtual}.georanking.site entrar no ar.`}
                </PendenteSalvar>
              ) : null}
            </Secao>
          </Controles>

          {/* ----------------- PREVIEW ----------------- */}
          <PreviewColuna>
            <PreviewSticky>
              <AbasDispositivo>
                <AbaDisp type="button" $sel={dispositivo === "celular"} onClick={() => setDispositivo("celular")}>
                  <span className="material-icons" style={{ fontSize: 16 }}>smartphone</span>Celular
                </AbaDisp>
                <AbaDisp type="button" $sel={dispositivo === "desktop"} onClick={() => setDispositivo("desktop")}>
                  <span className="material-icons" style={{ fontSize: 16 }}>desktop_windows</span>Desktop
                </AbaDisp>
              </AbasDispositivo>

              {/* Celular é o padrão: o mini-site é um link que circula no
                  WhatsApp e no Instagram, então é visto no telefone. */}
              <Moldura $celular={dispositivo === "celular"}>
                <MolduraTela>
                  <MiniSite t={tema} d={dados} preview />
                </MolduraTela>
              </Moldura>
              <PreviewNota>
                Pré-visualização real — é o mesmo componente que gera a página publicada.
              </PreviewNota>
            </PreviewSticky>
          </PreviewColuna>
        </Colunas>
      )}
    </Shell>
  );
}

export default EditorMinisite;

// ============================ estilos ============================
const Shell = styled.section`display: flex; flex-direction: column; gap: 16px;`;
const Barra = styled.div`display: flex; align-items: center; gap: 10px; flex-wrap: wrap;`;
const Titulo = styled.h2`margin: 0; flex: 1; font-size: 18px; font-weight: 600; color: #1F1F1F;`;
const BotaoVoltar = styled.button`
  display: inline-flex; align-items: center; gap: 5px; border: 1px solid #E2E6EA;
  background: #fff; color: #2B2D31; border-radius: 6px; padding: 7px 12px;
  font-size: 13px; font-weight: 600; cursor: pointer;
`;
const BotaoGhost = styled.button`
  display: inline-flex; align-items: center; gap: 5px; border: 1px solid #E2E6EA;
  background: #fff; color: #0B4FA8; border-radius: 6px; padding: 7px 12px;
  font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap;
`;
const BotaoPrimario = styled.button`
  background: #1264C7; border: none; color: #fff; border-radius: 6px;
  padding: 9px 18px; font-size: 13.5px; font-weight: 600; cursor: pointer;
  &:disabled { opacity: .5; cursor: default; }
`;
const BotaoLink = styled.a`
  align-self: flex-start; border: 1px solid #E2E6EA; background: #fff; color: #0B4FA8;
  border-radius: 6px; padding: 8px 14px; font-size: 13px; font-weight: 600; text-decoration: none;
`;
const Nota = styled.p`margin: 0; font-size: 13px; color: #5f6368;`;

const Colunas = styled.div`
  display: grid; grid-template-columns: minmax(0, 660px) 340px; gap: 28px;
  align-items: stretch; justify-content: center;
  width: 100%; max-width: 1030px; margin: 0 auto;
  @media (max-width: 1100px) { grid-template-columns: 1fr; max-width: 660px; }
`;
const Controles = styled.div`display: flex; flex-direction: column; gap: 18px; min-width: 0;`;
const Secao = styled.div`
  background: #fff; border: 1px solid #e5e7eb; border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15,23,42,.04); padding: 18px 20px 20px;
  display: flex; flex-direction: column; gap: 10px;
`;
const SecaoTitulo = styled.h3`
  margin: 0 0 2px; font-size: 14px; font-weight: 600; color: #1F1F1F;
  padding-left: 10px; border-left: 3px solid #1264C7;
`;
const GaleriaModelos = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 12px;
`;
const CardModelo = styled.button<{ $sel: boolean }>`
  padding: 0; background: #fff; cursor: pointer; text-align: left; overflow: hidden;
  border: 2px solid ${(p) => (p.$sel ? "#1264C7" : "#e5e7eb")};
  border-radius: 8px; display: flex; flex-direction: column;
`;
const MiniPreviewBox = styled.div`
  position: relative; width: 100%; aspect-ratio: 9 / 14; overflow: hidden; background: #f1f3f4;
`;
const MiniPreviewEscala = styled.div`
  position: absolute; top: 0; left: 0; width: 390px; height: 700px;
  transform: scale(0.30); transform-origin: top left; pointer-events: none;
`;
const CardModeloNome = styled.div`
  font-size: 11.5px; font-weight: 600; color: #1F1F1F; padding: 6px 8px 0;
  display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
`;
const Recomendado = styled.span`
  font-size: 9.5px; font-weight: 700; color: #1E8E3E; background: #EAF7EE;
  border-radius: 999px; padding: 1px 6px;
`;
const CardModeloNicho = styled.div`font-size: 10.5px; color: #80868b; padding: 1px 8px 7px;`;

const LinhaBotoes = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const Dica = styled.p`margin: 0; font-size: 11.5px; color: #80868b;`;
const Swatches = styled.div`display: flex; gap: 7px; flex-wrap: wrap;`;
const Swatch = styled.button<{ $cor: string; $sel: boolean }>`
  width: 26px; height: 26px; border-radius: 50%; cursor: pointer; background: ${(p) => p.$cor};
  border: 2px solid ${(p) => (p.$sel ? "#1F1F1F" : "rgba(0,0,0,.12)")};
`;
const Campo = styled.div``;
const Rotulo = styled.label`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 12px; font-weight: 600; color: #5f6368; margin-bottom: 4px;
`;
const Contador = styled.span<{ $ok: boolean }>`
  font-size: 10.5px; font-weight: 600;
  color: ${(p) => (p.$ok ? "#1E8E3E" : "#b06000")};
  background: ${(p) => (p.$ok ? "#EAF7EE" : "#FFF7E0")};
  border-radius: 999px; padding: 1px 7px;
`;
const Entrada = styled.input`
  flex: 1 1 200px; min-width: 0; max-width: 100%;
  border: 1px solid #E2E6EA; border-radius: 6px;
  padding: 9px 11px; font-size: 13.5px; color: #1F1F1F;
`;
const Area = styled.textarea`
  width: 100%; max-width: 560px;
  border: 1px solid #E2E6EA; border-radius: 6px; padding: 9px 11px;
  font-size: 13.5px; color: #1F1F1F; resize: vertical; font-family: inherit;
`;
const Select = styled.select`
  width: 100%; max-width: 360px; border: 1px solid #E2E6EA; border-radius: 6px;
  padding: 9px 10px; font-size: 13px; color: #1F1F1F; background: #fff;
`;
const Variacoes = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 10px; max-height: 460px; overflow-y: auto; padding-right: 4px;
  /* sem isto o grid divide a altura maxima entre as 12 linhas e achata os
     cartoes a 29px, escondendo o botao e o nome dentro do overflow */
  align-content: start; grid-auto-rows: max-content;
`;
const CartaoVariacao = styled.button<{ $sel: boolean }>`
  padding: 0; cursor: pointer; overflow: hidden; background: #fff; text-align: left;
  border: 2px solid ${(p) => (p.$sel ? "#1264C7" : "#e5e7eb")};
  border-radius: 8px; display: flex; flex-direction: column;
  transition: border-color .12s ease;
  &:hover { border-color: ${(p) => (p.$sel ? "#1264C7" : "#9aa0a6")}; }
`;
const VarAmostra = styled.div`
  padding: 11px 10px 12px; display: flex; flex-direction: column; gap: 6px; min-height: 74px;
`;
const VarTitulo = styled.div`
  font-size: 12.5px; font-weight: 700; line-height: 1.15; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
`;
const VarLinha = styled.div`height: 4px; width: 62%; border-radius: 3px; opacity: .5;`;
const VarBotao = styled.div`
  align-self: stretch; text-align: center; border-radius: 7px;
  padding: 5px 6px; font-size: 10px; font-weight: 700;
`;
const VarRodape = styled.div`
  display: flex; align-items: center; gap: 6px; justify-content: space-between;
  padding: 6px 8px 7px; border-top: 1px solid #f1f3f4; background: #fff;
  margin-top: auto;
`;
const VarNome = styled.span`
  font-size: 10px; line-height: 1.25; color: #5f6368; min-width: 0;
  /* nome inteiro em até 2 linhas: cortado em "Azul confiança …" não dizia nada */
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
const VarModo = styled.span<{ $escuro: boolean }>`
  flex: none; font-size: 8.5px; font-weight: 700; border-radius: 999px; padding: 1px 6px;
  color: ${(p) => (p.$escuro ? "#E2E6EA" : "#2B2D31")};
  background: ${(p) => (p.$escuro ? "#2B2D31" : "#f1f3f4")};
`;

const CapaAtual = styled.img`
  width: 100%; max-width: 420px; aspect-ratio: 1.91; object-fit: cover;
  border-radius: 8px; border: 1px solid #e5e7eb; display: block;
`;
const GradeCapas = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px; max-width: 560px;
`;
const CapaOpcao = styled.button`
  padding: 0; border: 2px solid #e5e7eb; border-radius: 8px; overflow: hidden;
  cursor: pointer; background: #f8fafc; line-height: 0;
  img { width: 100%; aspect-ratio: 1.5; object-fit: cover; display: block; }
  &:hover:not(:disabled) { border-color: #1264C7; }
  &:disabled { opacity: .55; cursor: default; }
`;
const Checklist = styled.ul`
  list-style: none; margin: 4px 0 6px; padding: 10px 12px; max-width: 520px;
  border: 1px solid #e5e7eb; border-radius: 8px; background: #fafbfc;
  display: flex; flex-direction: column; gap: 5px; font-size: 12.5px;
  li { display: flex; align-items: center; gap: 7px; }
`;
const ItemOpcional = styled.span`
  font-size: 10.5px; color: #80868b; border: 1px solid #e5e7eb;
  border-radius: 999px; padding: 0 6px;
`;
const Trilha = styled.nav`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  max-width: 1030px; margin: 0 auto; width: 100%;
  /* fica à vista durante toda a rolagem: é a bússola da tela */
  position: sticky; top: 0; z-index: 5;
  background: #f8fafc; padding: 8px 0 10px;
`;
const Passo = styled.button<{ $estado: "ok" | "falta" | "opcional" }>`
  display: inline-flex; align-items: center; gap: 6px; cursor: pointer;
  border-radius: 999px; padding: 5px 12px 5px 6px; font-size: 12.5px; font-weight: 600;
  background: #fff;
  border: 1px solid ${(p) => (p.$estado === "ok" ? "#a8dab5" : p.$estado === "falta" ? "#fdd663" : "#e5e7eb")};
  color: ${(p) => (p.$estado === "ok" ? "#1E8E3E" : p.$estado === "falta" ? "#7a4a00" : "#5f6368")};
  &:hover { border-color: #9aa0a6; }
`;
const PassoBola = styled.span<{ $estado: "ok" | "falta" | "opcional" }>`
  flex: none; width: 20px; height: 20px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  background: ${(p) => (p.$estado === "ok" ? "#EAF7EE" : p.$estado === "falta" ? "#FFF7E0" : "#f1f3f4")};
  color: inherit;
`;
const SecaoAjuda = styled.p`
  margin: -4px 0 2px; font-size: 12.5px; line-height: 1.45; color: #5f6368;
`;
const LinhaLink = styled.div`
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  padding: 8px; border: 1px solid #eceff1; border-radius: 8px; background: #fafbfc;
`;
const BotaoIcone = styled.button`
  flex: none; display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 6px; cursor: pointer;
  border: 1px solid #E2E6EA; background: #fff; color: #5f6368;
  &:hover:not(:disabled) { border-color: #9aa0a6; color: #1F1F1F; }
  &:disabled { opacity: .4; cursor: default; }
`;
const Pilula = styled.button<{ $sel: boolean }>`
  cursor: pointer; border-radius: 999px; padding: 6px 13px; font-size: 12.5px; font-weight: 600;
  border: 1px solid ${(p) => (p.$sel ? "#1264C7" : "#E2E6EA")};
  background: ${(p) => (p.$sel ? "#EAF1FB" : "#fff")};
  color: ${(p) => (p.$sel ? "#1a56c4" : "#2B2D31")};
  &:hover { border-color: ${(p) => (p.$sel ? "#1264C7" : "#9aa0a6")}; }
`;
const Deslizante = styled.input`
  width: 100%; max-width: 280px; accent-color: #1264C7; cursor: pointer;
`;
const PendenteSalvar = styled.p`
  display: flex; align-items: flex-start; gap: 7px; max-width: 520px;
  margin: 4px 0 0; font-size: 12.5px; line-height: 1.45; color: #7a4a00;
  background: #FFF7E0; border: 1px solid #fde293; border-radius: 8px; padding: 9px 11px;
`;
const PainelRevisao = styled.div<{ $ok: boolean }>`
  border-radius: 8px; padding: 12px 14px; max-width: 984px;
  border: 1px solid ${(p) => (p.$ok ? "#a8dab5" : "#fde293")};
  background: ${(p) => (p.$ok ? "#EAF7EE" : "#FFF7E0")};
  color: ${(p) => (p.$ok ? "#1E8E3E" : "#7a4a00")};
`;
const PainelTopo = styled.div`
  display: flex; align-items: center; gap: 8px;
  strong { font-size: 13px; font-weight: 600; flex: 1; }
`;
const FecharPainel = styled.button`
  border: none; background: transparent; color: inherit; cursor: pointer;
  padding: 0; display: inline-flex; opacity: .7;
  &:hover { opacity: 1; }
`;
const PainelNota = styled.p`margin: 6px 0 0 27px; font-size: 12px; opacity: .9;`;
const PainelLista = styled.ul`
  margin: 6px 0 0; padding-left: 44px; font-size: 12px;
  li { margin-bottom: 2px; }
`;
const LinhaSlug = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; max-width: 520px;
`;
const Sufixo = styled.span`font-size: 13px; color: #5f6368; white-space: nowrap;`;
const Check = styled.label`
  display: flex; gap: 10px; align-items: flex-start; cursor: pointer;
  input { margin-top: 3px; }
  strong { display: block; font-size: 13px; color: #1F1F1F; font-weight: 600; }
  small { display: block; font-size: 12px; color: #5f6368; margin-top: 2px; }
`;

const PreviewColuna = styled.div`
  min-width: 0;
  /* a coluna precisa ter a ALTURA da linha inteira, senão o sticky de dentro
     só gruda até o fim do próprio conteúdo e o preview desaparece ao rolar */
  align-self: stretch;
`;
const PreviewSticky = styled.div`
  position: sticky; top: 8px; display: flex; flex-direction: column; gap: 8px; align-items: center;
  @media (max-width: 1100px) { position: static; }
`;
const AbasDispositivo = styled.div`display: flex; gap: 6px; align-self: center;`;
const AbaDisp = styled.button<{ $sel: boolean }>`
  display: inline-flex; align-items: center; gap: 4px; cursor: pointer;
  border: 1px solid ${(p) => (p.$sel ? "#1264C7" : "#E2E6EA")};
  background: ${(p) => (p.$sel ? "#EAF1FB" : "#fff")};
  color: ${(p) => (p.$sel ? "#0B4FA8" : "#2B2D31")};
  border-radius: 999px; padding: 5px 12px; font-size: 12px; font-weight: 600;
`;
const Moldura = styled.div<{ $celular: boolean }>`
  width: ${(p) => (p.$celular ? "320px" : "100%")};
  max-width: 100%;
  border-radius: ${(p) => (p.$celular ? "26px" : "10px")};
  padding: ${(p) => (p.$celular ? "10px" : "6px")};
  background: #1F1F1F;
  box-shadow: 0 8px 26px rgba(15,23,42,.18);
`;
const MolduraTela = styled.div`
  height: 620px; overflow-y: auto; overflow-x: hidden;
  border-radius: inherit; background: #fff;
`;
const PreviewNota = styled.p`margin: 0; font-size: 11px; color: #80868b; text-align: center;`;
